/**
 * IconRenderer.ts
 * Scalable inline SVG injection and rendering helpers for American Dream: Pocket Life.
 * Zero external dependencies, 100% SSR-safe (no DOMParser reliance).
 */

import { FOOD_SVG_ICONS } from './FoodIcons';
import { CAREER_SVG_ICONS } from './CareerIcons';
import { SOUVENIR_SVG_ICONS } from './SouvenirIcons';
import {
  DECOR_CATEGORY_SVG_ICONS,
  DECOR_SOUVENIR_SVG_ICONS,
  FURNITURE_SVG_ICONS,
  FURNITURE_PNG_ASSETS,
} from './DecorIcons';
import { LIFE_EVENT_SVG_ICONS } from './LifeEventIcons';
import { UI_SYSTEM_SVG_ICONS } from './UiSystemIcons';
import { getSouvenirEntry } from '../../content/StateSouvenirsRegistry';

export * from './CountyIndustryIcons';
export * from './CountySpecialtyIcons';
export * from './StateLandmarkIcons';

export interface IconRenderOptions {
  size?: number;
  sizePx?: number;
  width?: number;
  height?: number;
  className?: string;
  title?: string;
  idSuffix?: string;
}

export type UiGouacheIconKey =
  | 'icon_atlas_compass'
  | 'icon_career_briefcase'
  | 'icon_pantry_platter'
  | 'icon_decor_palette'
  | 'icon_home_cottage'
  | 'icon_cash_wallet'
  | 'icon_energy_bolt'
  | 'icon_gps_checkin'
  | 'icon_time_day'
  | 'icon_time_sunset'
  | 'icon_time_night'
  | 'icon_trophy_medal'
  | 'icon_mood_happy'
  | 'icon_mood_low'
  | 'icon_shop_basket'
  | 'icon_check_seal'
  | 'icon_lock_padlock'
  | 'icon_undo_arrow'
  | 'icon_rotate_turn'
  | 'icon_storage_box';

export const UI_GOUACHE_ICON_URLS: Readonly<Record<UiGouacheIconKey, string>> = {
  icon_atlas_compass: './assets/ui_icons/icon_atlas_compass.png',
  icon_career_briefcase: './assets/ui_icons/icon_career_briefcase.png',
  icon_pantry_platter: './assets/ui_icons/icon_pantry_platter.png',
  icon_decor_palette: './assets/ui_icons/icon_decor_palette.png',
  icon_home_cottage: './assets/ui_icons/icon_home_cottage.png',
  icon_cash_wallet: './assets/ui_icons/icon_cash_wallet.png',
  icon_energy_bolt: './assets/ui_icons/icon_energy_bolt.png',
  icon_gps_checkin: './assets/ui_icons/icon_gps_checkin.png',
  icon_time_day: './assets/ui_icons/icon_time_day.png',
  icon_time_sunset: './assets/ui_icons/icon_time_sunset.png',
  icon_time_night: './assets/ui_icons/icon_time_night.png',
  icon_trophy_medal: './assets/ui_icons/icon_trophy_medal.png',
  icon_mood_happy: './assets/ui_icons/icon_mood_happy.png',
  icon_mood_low: './assets/ui_icons/icon_mood_low.png',
  icon_shop_basket: './assets/ui_icons/icon_shop_basket.png',
  icon_check_seal: './assets/ui_icons/icon_check_seal.png',
  icon_lock_padlock: './assets/ui_icons/icon_lock_padlock.png',
  icon_undo_arrow: './assets/ui_icons/icon_undo_arrow.png',
  icon_rotate_turn: './assets/ui_icons/icon_rotate_turn.png',
  icon_storage_box: './assets/ui_icons/icon_storage_box.png',
};

export function getUiGouacheIconUrl(key: UiGouacheIconKey | string): string | null {
  if (typeof key !== 'string' || !key.trim()) return null;
  const clean = key.trim() as UiGouacheIconKey;
  return UI_GOUACHE_ICON_URLS[clean] || null;
}

export interface GouacheUiIconRenderOptions extends IconRenderOptions {
  /** Defaults to true when placed alongside visible button/HUD label text so screen readers do not double-announce */
  decorative?: boolean;
}

function escapeHtmlAttr(raw: string): string {
  return raw
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Renders a crisp `<img>` pointing to a hand-drawn 512x512 RGBA Storybook Gouache UI icon `.png`
 * stored in `public/assets/ui_icons/` (never procedural `<canvas>`).
 */
export function renderUiGouacheIcon(
  key: UiGouacheIconKey,
  options: GouacheUiIconRenderOptions = {}
): string {
  const url = getUiGouacheIconUrl(key);
  if (!url) return '';
  const clampPx = (val: number | undefined, fallback: number): number =>
    typeof val === 'number' && Number.isFinite(val) && val > 0 ? Math.round(val) : fallback;
  const baseSize = clampPx(options.size ?? options.sizePx, 24);
  const width = clampPx(options.width, baseSize);
  const height = clampPx(options.height, baseSize);
  const safeClass = options.className ? ` ${escapeHtmlAttr(options.className)}` : '';
  const isDecorative = options.decorative !== false;
  const escapedTitle = options.title ? escapeHtmlAttr(options.title) : '';
  const altAttr = isDecorative
    ? ' alt="" aria-hidden="true"'
    : ` alt="${escapedTitle || escapeHtmlAttr(String(key).trim())}"`;
  const titleAttr = escapedTitle ? ` title="${escapedTitle}"` : '';
  const idAttr = options.idSuffix
    ? ` id="ui_png_${options.idSuffix.replace(/[^a-zA-Z0-9_-]/g, '_')}"`
    : '';
  return `<img${idAttr} src="${url}"${altAttr}${titleAttr} width="${width}" height="${height}" class="ui-gouache-png-icon${safeClass}" decoding="async" draggable="false" />`;
}

/**
 * Injects width, height, CSS classes, title, and optional scoped gradient IDs cleanly into any SVG string.
 */
export function renderSvg(
  svgString: string,
  options: IconRenderOptions = {}
): string {
  if (!svgString) return '';
  const size = options.size ?? options.sizePx;
  const width = options.width ?? size;
  const height = options.height ?? size;
  const extraClass = options.className ? ` ${options.className}` : '';
  const titleAttr = options.title ? ` title="${options.title}"` : '';

  let result = svgString.trim();
  if (!/^<svg\b/i.test(result)) {
    return result;
  }

  // Scope internal <defs> IDs when idSuffix is provided to prevent duplicate DOM IDs
  if (options.idSuffix) {
    const suffix = options.idSuffix.replace(/[^a-zA-Z0-9_-]/g, '_');
    result = result
      .replace(/\bid="([^"]+)"/g, `id="$1_${suffix}"`)
      .replace(/url\(#([^)]+)\)/g, `url(#$1_${suffix})`);
  }

  // 1. Ensure/Inject class
  if (/class="[^"]*"/i.test(result)) {
    if (extraClass) {
      result = result.replace(/class="([^"]*)"/i, `class="$1${extraClass}"`);
    }
  } else {
    result = result.replace(/^<svg\b/i, `<svg class="ui-svg-icon${extraClass}"`);
  }

  // 2. Ensure/Inject width
  if (width !== undefined) {
    if (/width="[^"]*"/i.test(result)) {
      result = result.replace(/width="[^"]*"/i, `width="${width}"`);
    } else {
      result = result.replace(/^<svg\b/i, `<svg width="${width}"`);
    }
  }

  // 3. Ensure/Inject height
  if (height !== undefined) {
    if (/height="[^"]*"/i.test(result)) {
      result = result.replace(/height="[^"]*"/i, `height="${height}"`);
    } else {
      result = result.replace(/^<svg\b/i, `<svg height="${height}"`);
    }
  }

  // 4. Inject title if provided
  if (titleAttr && !/title="[^"]*"/i.test(result)) {
    result = result.replace(/^<svg\b/i, `<svg${titleAttr}`);
  }

  return result;
}

/**
 * Alias of `renderSvg` for modular icon registries.
 */
export const formatSvgIcon = renderSvg;

/**
 * Returns the illustrated SVG for any of the 15 regional foods.
 */
export function getFoodIconSvg(
  foodId: string,
  options?: IconRenderOptions
): string {
  const rawSvg = FOOD_SVG_ICONS[foodId] || UI_SYSTEM_SVG_ICONS.utensils;
  return renderSvg(rawSvg, { size: 32, ...options });
}

/**
 * Returns the illustrated SVG for any starter job or state career track.
 */
export function getCareerIconSvg(
  careerOrJobId: string,
  options?: IconRenderOptions
): string {
  if (!careerOrJobId) {
    return renderSvg(UI_SYSTEM_SVG_ICONS.briefcase, { size: 24, ...options });
  }

  // Exact ID match
  if (CAREER_SVG_ICONS[careerOrJobId]) {
    return renderSvg(CAREER_SVG_ICONS[careerOrJobId], { size: 24, ...options });
  }

  // Title / Keyword mapping
  const lower = careerOrJobId.toLowerCase();
  if (lower.includes('dish') || lower.includes('rửa bát')) {
    return renderSvg(CAREER_SVG_ICONS.job_starter_dishwasher, { size: 24, ...options });
  }
  if (lower.includes('courier') || lower.includes('delivery') || lower.includes('giao hàng') || lower.includes('shipper')) {
    return renderSvg(CAREER_SVG_ICONS.job_starter_courier, { size: 24, ...options });
  }
  if (lower.includes('barista') || lower.includes('cafe') || lower.includes('cà phê') || lower.includes('pha chế')) {
    return renderSvg(CAREER_SVG_ICONS.job_starter_barista, { size: 24, ...options });
  }
  if (lower.includes('wall_street') || lower.includes('wall street') || lower.includes('analyst') || lower.includes('cfa') || lower.includes('quant')) {
    return renderSvg(CAREER_SVG_ICONS.ny_wall_street, { size: 24, ...options });
  }
  if (lower.includes('broadway') || lower.includes('producer') || lower.includes('stage') || lower.includes('theater')) {
    return renderSvg(CAREER_SVG_ICONS.ny_broadway, { size: 24, ...options });
  }
  if (lower.includes('ai') || lower.includes('engineer') || lower.includes('silicon') || lower.includes('deep learning')) {
    return renderSvg(CAREER_SVG_ICONS.ca_ai_engineer, { size: 24, ...options });
  }
  if (lower.includes('gamedev') || lower.includes('game') || lower.includes('pixel') || lower.includes('indie')) {
    return renderSvg(CAREER_SVG_ICONS.ca_indie_gamedev, { size: 24, ...options });
  }
  if (lower.includes('energy') || lower.includes('tycoon') || lower.includes('power') || lower.includes('oil')) {
    return renderSvg(CAREER_SVG_ICONS.tx_energy_tycoon, { size: 24, ...options });
  }
  if (lower.includes('pitmaster') || lower.includes('bbq') || lower.includes('smoke') || lower.includes('brisket')) {
    return renderSvg(CAREER_SVG_ICONS.tx_pitmaster, { size: 24, ...options });
  }
  if (lower.includes('aerospace') || lower.includes('shuttle') || lower.includes('rocket') || lower.includes('space') || lower.includes('flight')) {
    return renderSvg(CAREER_SVG_ICONS.fl_aerospace, { size: 24, ...options });
  }
  if (lower.includes('resort') || lower.includes('hotel') || lower.includes('manager') || lower.includes('hospitality')) {
    return renderSvg(CAREER_SVG_ICONS.fl_resort_manager, { size: 24, ...options });
  }
  if (lower.includes('cloud') || lower.includes('architect') || lower.includes('aws') || lower.includes('server')) {
    return renderSvg(CAREER_SVG_ICONS.wa_cloud_architect, { size: 24, ...options });
  }
  if (lower.includes('roaster') || lower.includes('cupping') || lower.includes('specialty coffee')) {
    return renderSvg(CAREER_SVG_ICONS.wa_specialty_roaster, { size: 24, ...options });
  }

  return renderSvg(UI_SYSTEM_SVG_ICONS.briefcase, { size: 24, ...options });
}

/**
 * Returns the illustrated 2.5D SVG for any of the 5 state souvenirs (preserved for SVG vector contracts).
 */
export function getSouvenirIconSvg(
  souvenirId: string,
  options?: IconRenderOptions
): string {
  if (!souvenirId) {
    return renderSvg(UI_SYSTEM_SVG_ICONS.trophy, { size: 28, ...options });
  }

  if (SOUVENIR_SVG_ICONS[souvenirId]) {
    return renderSvg(SOUVENIR_SVG_ICONS[souvenirId], { size: 28, ...options });
  }

  // State ID / name fallback
  const upper = souvenirId.toUpperCase();
  if (upper.includes('NY') || upper.includes('LIBERTY') || upper.includes('STATUE')) {
    return renderSvg(SOUVENIR_SVG_ICONS.souvenir_ny_statue_liberty, { size: 28, ...options });
  }
  if (upper.includes('CA') || upper.includes('GOLDEN') || upper.includes('BRIDGE')) {
    return renderSvg(SOUVENIR_SVG_ICONS.souvenir_ca_golden_gate, { size: 28, ...options });
  }
  if (upper.includes('TX') || upper.includes('LONGHORN') || upper.includes('HAT') || upper.includes('STETSON')) {
    return renderSvg(SOUVENIR_SVG_ICONS.souvenir_tx_longhorn, { size: 28, ...options });
  }
  if (upper.includes('FL') || upper.includes('ROCKET') || upper.includes('FLAMINGO') || upper.includes('SHUTTLE')) {
    return renderSvg(SOUVENIR_SVG_ICONS.souvenir_fl_rocket_flamingo, { size: 28, ...options });
  }
  if (upper.includes('WA') || upper.includes('NEEDLE') || upper.includes('SEATTLE')) {
    return renderSvg(SOUVENIR_SVG_ICONS.souvenir_wa_space_needle, { size: 28, ...options });
  }

  return renderSvg(UI_SYSTEM_SVG_ICONS.trophy, { size: 28, ...options });
}

/**
 * Resolves the hand-drawn 2.5D Cozy Storybook Gouache PNG URL (`512x512` transparent RGBA)
 * for any souvenir ID, legacy alias, or 2-letter US state code (all 50 states + DC).
 */
export function getSouvenirPngUrl(souvenirIdOrStateCode: string): string | null {
  if (!souvenirIdOrStateCode) return null;
  const entry = getSouvenirEntry(souvenirIdOrStateCode);
  return entry ? entry.pngUrl : null;
}

/**
 * Renders a crisp `<img>` pointing to the hand-drawn 2.5D Cozy Storybook Gouache State Souvenir `.png`
 * (`512x512` native HD, `object-fit: contain`), falling back to `getSouvenirIconSvg` only when unrecognized.
 */
export function renderSouvenirVisual(
  souvenirIdOrStateCode: string,
  options?: IconRenderOptions
): string {
  const entry = getSouvenirEntry(souvenirIdOrStateCode);
  if (entry) {
    const size = options?.size ?? options?.sizePx ?? 38;
    const width = options?.width ?? size;
    const height = options?.height ?? size;
    const extraClass = options?.className ? ` ${options.className}` : '';
    const resolvedTitle = options?.title || entry.name;
    const escapedTitle = resolvedTitle.replace(/"/g, '&quot;');
    const titleAttr = ` title="${escapedTitle}"`;
    const idAttr = options?.idSuffix
      ? ` id="souv_png_${options.idSuffix.replace(/[^a-zA-Z0-9_-]/g, '_')}"`
      : '';
    return `<img${idAttr} src="${entry.pngUrl}" alt="${escapedTitle}" width="${width}" height="${height}" class="ui-souvenir-png-icon ui-furniture-png-icon${extraClass}"${titleAttr} decoding="async" style="width:${width}px;height:${height}px;object-fit:contain;display:block;" />`;
  }
  return getSouvenirIconSvg(souvenirIdOrStateCode, options);
}

/**
 * Resolves the hand-drawn TapBlaze / Cats & Soup PNG sprite URL for a furniture or souvenir item if available.
 */
export function getDecorSpritePngUrl(typeId: string, tier: number = 1): string | null {
  if (!typeId) return null;
  const normalizedType = typeId.trim().toLowerCase();

  if (FURNITURE_PNG_ASSETS[normalizedType]) {
    return FURNITURE_PNG_ASSETS[normalizedType];
  }

  const spriteKeyAliasMap: Record<string, string> = {
    plant_cactus: 'plant_t1',
    plant_succulent: 'plant_t1',
    plant_monstera: 'plant_t2',
    plant_ficus: 'plant_t2',
    plant_bonsai: 'plant_t3',
    souvenir_ny_statue: 'souvenir_ny_statue_liberty',
    souvenir_fl_rocket: 'souvenir_fl_rocket_flamingo',
    souvenir_ca_bridge: 'souvenir_ca_golden_gate',
    souvenir_tx_hat: 'souvenir_tx_longhorn',
    souvenir_wa_needle: 'souvenir_wa_space_needle',
  };
  const aliasedKey = spriteKeyAliasMap[normalizedType];
  if (aliasedKey && FURNITURE_PNG_ASSETS[aliasedKey]) {
    return FURNITURE_PNG_ASSETS[aliasedKey];
  }

  if (normalizedType.startsWith('souvenir_')) {
    const souvEntry = getSouvenirEntry(normalizedType);
    if (souvEntry) {
      return souvEntry.pngUrl;
    }
  }

  const tierKey = `${normalizedType}_t${tier || 1}`;
  if (FURNITURE_PNG_ASSETS[tierKey]) {
    return FURNITURE_PNG_ASSETS[tierKey];
  }

  return null;
}

/**
 * Renders an `<img>` element pointing to the hand-drawn `.png` asset if available,
 * falling back to `getDecorIconSvg` when a raster asset is not registered.
 */
export function renderDecorFurnitureVisual(
  typeId: string,
  tierOrOptions: number | IconRenderOptions = 1,
  maybeOptions?: IconRenderOptions
): string {
  const tier = typeof tierOrOptions === 'number' ? tierOrOptions : 1;
  const options = typeof tierOrOptions === 'object' && tierOrOptions !== null ? tierOrOptions : maybeOptions;

  const pngUrl = getDecorSpritePngUrl(typeId, tier);
  if (pngUrl) {
    const size = options?.size ?? options?.sizePx ?? 44;
    const width = options?.width ?? size;
    const height = options?.height ?? size;
    const extraClass = options?.className ? ` ${options.className}` : '';
    const titleAttr = options?.title ? ` title="${options.title}"` : '';
    const idAttr = options?.idSuffix ? ` id="decor_png_${options.idSuffix.replace(/[^a-zA-Z0-9_-]/g, '_')}"` : '';
    return `<img${idAttr} src="${pngUrl}" alt="${options?.title || typeId}" width="${width}" height="${height}" class="ui-furniture-png-icon${extraClass}"${titleAttr} style="width:${width}px;height:${height}px;object-fit:contain;display:block;" />`;
  }
  return getDecorIconSvg(typeId, tier, options);
}

/**
 * Returns the illustrated SVG for furniture categories, catalog spriteKeys, souvenirs, or catalog items by tier.
 */
export function getDecorIconSvg(
  typeId: string,
  tierOrOptions: number | IconRenderOptions = 1,
  maybeOptions?: IconRenderOptions
): string {
  const tier = typeof tierOrOptions === 'number' ? tierOrOptions : 1;
  const options = typeof tierOrOptions === 'object' && tierOrOptions !== null ? tierOrOptions : maybeOptions;

  if (!typeId) {
    return renderSvg(UI_SYSTEM_SVG_ICONS.navDecor, { size: 24, ...options });
  }

  const normalizedType = typeId.toLowerCase();

  // Direct match (e.g. 'bed_t1', 'fridge_t2', 'plant_t3')
  if (FURNITURE_SVG_ICONS[normalizedType]) {
    return renderSvg(FURNITURE_SVG_ICONS[normalizedType], { size: 24, ...options });
  }

  // Souvenir direct match
  if (DECOR_SOUVENIR_SVG_ICONS[normalizedType]) {
    return renderSvg(DECOR_SOUVENIR_SVG_ICONS[normalizedType], { size: 24, ...options });
  }
  if (SOUVENIR_SVG_ICONS[normalizedType]) {
    return renderSvg(SOUVENIR_SVG_ICONS[normalizedType], { size: 24, ...options });
  }

  // Plant & souvenir spriteKey aliases
  const spriteKeyAliasMap: Record<string, string> = {
    plant_cactus: 'plant_t1',
    plant_succulent: 'plant_t1',
    plant_monstera: 'plant_t2',
    plant_ficus: 'plant_t2',
    plant_bonsai: 'plant_t3',
  };
  if (spriteKeyAliasMap[normalizedType] && FURNITURE_SVG_ICONS[spriteKeyAliasMap[normalizedType]]) {
    return renderSvg(FURNITURE_SVG_ICONS[spriteKeyAliasMap[normalizedType]], { size: 24, ...options });
  }

  const tierKey = `${normalizedType}_t${tier || 1}`;
  if (FURNITURE_SVG_ICONS[tierKey]) {
    return renderSvg(FURNITURE_SVG_ICONS[tierKey], { size: 24, ...options });
  }

  // Category fallback
  const catUpper = typeId.toUpperCase();
  if (DECOR_CATEGORY_SVG_ICONS[catUpper]) {
    return renderSvg(DECOR_CATEGORY_SVG_ICONS[catUpper], { size: 24, ...options });
  }

  return renderSvg(UI_SYSTEM_SVG_ICONS.navDecor, { size: 24, ...options });
}

export const getDecorSpriteSvg = getDecorIconSvg;

/**
 * Returns the illustrated SVG for any of the 10 dynamic life events.
 */
export function getLifeEventIconSvg(
  eventId: string,
  options?: IconRenderOptions
): string {
  const rawSvg = LIFE_EVENT_SVG_ICONS[eventId] || UI_SYSTEM_SVG_ICONS.sparkles;
  return renderSvg(rawSvg, { size: 32, ...options });
}

